import { Flight } from "@/models/flight";
import { getDistance } from "@/utils/airport_distance";

import {
  Box,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  Button,
} from "@chakra-ui/react";
import { EmailSignUp } from "./EmailSignUp";
import { t } from "i18next";
import { DailyPromoSheet } from "../Daily/DailyPromoSheet";
import { DailyPromoButton } from "../DailyPromoButton/DailyPromoButton";
import { shouldOfferDaily } from "@/daily/promo";
import { useState } from "react";

interface GameSummaryProps {
  points: number;
  flights: Flight[];
  onResetGame: () => void;
}

export const GameSummary = ({
  points,
  flights,
  onResetGame,
}: GameSummaryProps) => {
  const totalDistance = flights.reduce((acc, flight) => {
    return acc + getDistance(flight.origin_airport, flight.destination_airport);
  }, 0);

  const originCountrySet = new Set(
    flights.map((flight) => flight.origin_airport.country_code)
  );

  const destinationCountrySet = new Set(
    flights.map((flight) => flight.destination_airport.country_code)
  );

  const countryCount = new Set([...originCountrySet, ...destinationCountrySet])
    .size;

  const originCountryEmojis = new Set(
    flights.map((flight) => flight.origin_airport.country_emoji)
  );

  const destinationCountryEmojis = new Set(
    flights.map((flight) => flight.destination_airport.country_emoji)
  );

  const countryEmojis = Array.from(
    new Set([...originCountryEmojis, ...destinationCountryEmojis])
  );

  const airlineCount = new Set(flights.map((flight) => flight.airline)).size;

  // Players who have not played today's daily puzzle are offered it.
  const [offerDaily] = useState(shouldOfferDaily);

  return (
    <>
      {/* Centred when there is room; on a short screen it starts at the top
          and scrolls, so the score is never pushed out of view. */}
      <VStack w="100%" my="auto" py={{ base: 2, md: 6 }}>
        <Text
          color="white"
          textAlign="center"
          fontSize={{ base: "4xl", md: "7xl" }}
          border="2px solid white"
          borderRadius="xl"
          p={{ base: 4, md: 10 }}
          mb={{ base: 0, md: 4 }}
          background="whiteAlpha.200"
        >
          {points.toLocaleString(navigator.language)}
        </Text>
        <Heading
          color="white"
          textAlign="center"
          fontSize={{ base: "xl", md: "3xl" }}
        >
          {t("game.gameSummary.totalPoints")}
        </Heading>

        <Grid templateColumns="1fr" gap={6} p={4} alignItems="center">
          <GridItem>
            <Text
              color="white"
              fontSize="2xl"
              textAlign="center"
              fontFamily="sans-serif"
              fontWeight="bold"
            >
              {t("game.gameSummary.tripDetails", {
                distance: totalDistance.toLocaleString(navigator.language),
                countriesString: `${countryCount} ${
                  countryCount > 1
                    ? t("game.gameSummary.countryPlural")
                    : t("game.gameSummary.countrySingular")
                } (${countryEmojis.join(" ")})`,
                airlinesString: `${airlineCount} ${
                  airlineCount > 1
                    ? t("game.gameSummary.airlinePlural")
                    : t("game.gameSummary.airlineSingular")
                }`,
              })}
            </Text>
          </GridItem>
        </Grid>
        <Button
          background="green.600"
          size="xl"
          onClick={onResetGame}
          w={{ base: "100%", md: "60%" }}
          h="70px"
          borderRadius="10px"
          fontSize="2xl"
          mt={10}
          _hover={{ background: "green.700" }}
        >
          {t("game.gameSummary.newGame")}
        </Button>

        {offerDaily && (
          <Box w={{ base: "100%", md: "60%" }}>
            <DailyPromoButton from="classic_end_screen" />
          </Box>
        )}

        <EmailSignUp />
      </VStack>
      {offerDaily && <DailyPromoSheet />}
    </>
  );
};
